import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import HeroWorkShowcase from './HeroWorkShowcase';
import { CtaRow, GhostCta, PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';

const CAPABILITIES = [
  { title: 'AI films & video', copy: 'Describe your film and let Gab AI Work Agent bring the script, scenes, music, and voiceover together. Spend your time on the vision.' },
  { title: 'Research & analysis', copy: 'Hand over the question. Get research, source comparisons, and a brief, deck, or spreadsheet without opening every tab yourself.' },
  { title: 'Documents & presentations', copy: 'Skip the blank page. Get reports, proposals, budgets, and slide decks in editable formats, ready for your review.' },
  { title: 'Websites & code', copy: 'Get a website built, an idea prototyped, or a repetitive task scripted. Gab AI Work Agent writes and runs the code; you keep the source.' },
  { title: 'Playable games', copy: 'Let AI build the game you have in mind, from controls to scoring. Jump into the preview, ask for changes, and make time to play.' },
  { title: 'Projects with many steps', copy: 'Delegate the research, writing, design, and production together. Follow the progress and review the result without coordinating every step yourself.' },
];

function LandingWorkAgent() {
  const { c, isXS, isCompact, width } = useLanding();
  const [showExample, setShowExample] = useState(false);
  const size = fluid(width, 34, 48);
  return (
    <Section label="Gab AI Work Agent" style={{ borderTopWidth: 1, borderTopColor: c.rule }} contentStyle={{ paddingTop: fluid(width, 48, 80), paddingBottom: fluid(width, 48, 80) }}>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 34 : 80 }}>
        <View style={{ flex: isCompact ? undefined : 0.85, minWidth: 0 }}>
          <Text style={{ color: c.accent, fontSize: 15, marginBottom: 20 }}>Gab AI Work Agent</Text>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.1), letterSpacing: -size * 0.035 }}>Hand off more than a single task.</Text>
          <Text style={{ color: c.inkSoft, fontSize: 17, lineHeight: 28, marginTop: 24, maxWidth: 390 }}>Give Gab AI Work Agent the goal. With its own computer, browser, and 100+ AI models, it plans the steps, does the work, and delivers the files. Let it handle the time-consuming parts while you get on with your day.</Text>
          <CtaRow style={{ marginTop: 28 }}><PrimaryCta restrained label="Get Gab AI Work Agent with Plus" full={isXS} /></CtaRow>
          <GhostCta label="View plans" href="/pricing" style={{ marginTop: 8 }} />
        </View>
        <View style={{ flex: isCompact ? undefined : 1.3, minWidth: 0 }}>
          {CAPABILITIES.map((item, index) => (
            <View key={item.title} style={{ paddingTop: index === 0 ? 0 : 23, paddingBottom: 23, borderBottomWidth: 1, borderBottomColor: c.rule }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 22, lineHeight: 29, letterSpacing: -0.5 }}>{item.title}</Text>
              <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, marginTop: 9 }}>{item.copy}</Text>
            </View>
          ))}
        </View>
      </View>
      <View style={{ marginTop: 36, borderTopWidth: 1, borderTopColor: c.rule }}>
        <Pressable
          accessibilityRole="button"
          {...(isWeb ? { 'aria-expanded': showExample, 'aria-controls': 'work-example' } : { accessibilityState: { expanded: showExample } })}
          onPress={() => setShowExample(value => !value)}
          style={{ minHeight: 68, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 20, ...(isWeb ? { cursor: 'pointer' } : {}) }}
        >
          <Text weight="medium" style={{ color: c.ink, fontSize: 15, flex: 1 }}>{showExample ? 'Close the example workflows' : 'See how Gab AI Work Agent approaches a project'}</Text>
          <Icon name={showExample ? 'minus' : 'plus'} size={14} color={c.ink} />
        </Pressable>
        {showExample ? <View nativeID="work-example" style={{ marginTop: 8 }}><HeroWorkShowcase /></View> : null}
      </View>
    </Section>
  );
}
export default React.memo(LandingWorkAgent);
